
import type React from "react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/hooks/use-toast"
import { createGym } from "@/api/gyms"
import { useUser } from "@/contexts/user-context"
import { useGym } from "@/contexts/gym-context"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

interface AddGymDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onGymAdded?: () => void
}

export function AddGymDialog({ open, onOpenChange, onGymAdded }: AddGymDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const { fetchUserData } = useUser()
  const { fetchGyms, selectGym } = useGym()
  const navigate = useNavigate()
  const [status, setStatus] = useState<"active" | "inactive">("active")

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    // Get form data
    const formData = new FormData(e.currentTarget)

    const gymData = {
      name: formData.get("name")?.toString(),
      address: formData.get("address")?.toString(),
      city: formData.get("city")?.toString(),
      country: formData.get("country")?.toString(),
      phone: formData.get("phone")?.toString(),
      email: formData.get("email")?.toString(),
      workingHours: formData.get("workingHours")?.toString(),
      facilities: formData.get("facilities")?.toString(), // Keep as string
      status,
    }

    try {
      // Make API call to create gym
      const response = await createGym(gymData)

      // Get the gym ID
      const gymId = response._id

      // Close the dialog
      onOpenChange(false)

      toast({
        title: t("common.success"),
        description: t("gyms.gymAdded"),
      })

      // Refresh user data to get updated gym array
      await fetchUserData()

      // Refresh gyms list - using fetchGyms instead of refetchGyms
      if (fetchGyms) {
        await fetchGyms()
      }

      // Set the newly created gym as the selected gym
      if (selectGym) {
        selectGym(gymId)
      }

      // Call the callback if provided
      if (onGymAdded) {
        onGymAdded()
      }

      // Redirect to dashboard and force a refresh
      navigate("/dashboard")
      window.location.reload()
    } catch (error) {
      console.error("Failed to create gym:", error)
      toast({
        title: t("common.error"),
        description: t("gyms.errorAddingGym"),
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("gyms.addGym")}</DialogTitle>
          <DialogDescription>{t("gyms.addGymDescription")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                {t("gyms.name")}
              </Label>
              <Input id="name" name="name" className="col-span-3" required />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="address" className="text-right">
                {t("gyms.address")}
              </Label>
              <Input id="address" name="address" className="col-span-3" required />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="city" className="text-right">
                {t("gyms.city")}
              </Label>
              <Input id="city" name="city" className="col-span-3" required />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="country" className="text-right">
                {t("gyms.country")}
              </Label>
              <Input id="country" name="country" className="col-span-3" required />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="phone" className="text-right">
                {t("gyms.phone")}
              </Label>
              <Input id="phone" name="phone" className="col-span-3" required />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                {t("gyms.email")}
              </Label>
              <Input id="email" name="email" type="email" className="col-span-3" required />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="workingHours" className="text-right">
                {t("gyms.workingHours")}
              </Label>
              <Input
                id="workingHours"
                name="workingHours"
                className="col-span-3"
                placeholder="Mon-Fri: 6:00-22:00, Sat-Sun: 8:00-20:00"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="facilities" className="text-right">
                {t("gyms.facilities")}
              </Label>
              <Textarea
                id="facilities"
                name="facilities"
                className="col-span-3"
                placeholder="Cardio Equipment, Weight Training, Pool, etc."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="status" className="text-right">
                {t("gyms.status")}
              </Label>
              <Select value={status} onValueChange={(value) => setStatus(value as "active" | "inactive")}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder={t("gyms.selectStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t("gyms.active")}</SelectItem>
                  <SelectItem value="inactive">{t("gyms.inactive")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? t("common.loading") : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
